import * as React from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { getMyAdminStatus } from "@/lib/billing.functions";
import {
  createReferralCode,
  listReferralCodes,
  setReferralCodeActive,
  type ReferralCodeRow,
} from "@/lib/referrals.functions";

export const Route = createFileRoute("/_authed/admin/referrals")({
  head: () => ({
    meta: [
      { title: "Admin referrals: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: AdminReferralsPage,
});

function AdminReferralsPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listReferralCodes);
  const createFn = useServerFn(createReferralCode);
  const toggleFn = useServerFn(setReferralCodeActive);

  const rows = useQuery({
    queryKey: ["admin-referral-codes"],
    queryFn: () => listFn(),
  });

  const [code, setCode] = React.useState("");
  const [influencer, setInfluencer] = React.useState("");
  const [bonus, setBonus] = React.useState("25");
  const [expiresAt, setExpiresAt] = React.useState("");
  const [success, setSuccess] = React.useState<string | null>(null);
  const [duplicate, setDuplicate] = React.useState(false);

  const create = useMutation({
    mutationFn: async () => {
      const normalized = code.trim().toUpperCase();
      if (/\s/.test(normalized)) {
        throw new Error("Code cannot contain whitespace.");
      }
      return createFn({
        data: {
          code: normalized,
          influencer_name: influencer.trim(),
          bonus_pct: parseInt(bonus, 10) || 0,
          expires_at: expiresAt
            ? new Date(`${expiresAt}T00:00:00Z`).toISOString()
            : null,
        },
      });
    },
    onSuccess: (res) => {
      if (!res.ok) {
        setDuplicate(true);
        return;
      }
      setSuccess(`Created ${code.trim().toUpperCase()}.`);
      setCode("");
      setInfluencer("");
      setBonus("25");
      setExpiresAt("");
      qc.invalidateQueries({ queryKey: ["admin-referral-codes"] });
    },
  });

  return (
    <main className="mx-auto max-w-[960px] px-4 pb-16 pt-6">
      <header className="mb-6">
        <Link
          to="/admin"
          className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to admin
        </Link>
        <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          Referrals.
        </h1>
        <p className="mt-1 text-[14px] text-brand-muted">
          Create referral codes and review redemption history. Codes are never
          deleted so payout history stays intact. Deactivate a code with the
          Active toggle.
        </p>
      </header>

      <section className="mb-8 rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="mb-4 font-serif text-[20px] text-brand-ink">
          Create a code
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSuccess(null);
            setDuplicate(false);
            create.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <Label htmlFor="rc-code">Code</Label>
            <Input
              id="rc-code"
              required
              maxLength={64}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="ACME25"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              style={{ textTransform: "uppercase" }}
            />
            <p className="mt-1 text-[11.5px] text-brand-muted">
              Stored uppercase. No spaces. 64 characters or fewer.
            </p>
          </div>
          <div>
            <Label htmlFor="rc-name">Influencer name</Label>
            <Input
              id="rc-name"
              required
              maxLength={200}
              value={influencer}
              onChange={(e) => setInfluencer(e.target.value)}
              placeholder="Acme Media LLC"
            />
          </div>
          <div>
            <Label htmlFor="rc-bonus">Bonus percent</Label>
            <Input
              id="rc-bonus"
              type="number"
              min={1}
              max={100}
              required
              value={bonus}
              onChange={(e) => setBonus(e.target.value)}
            />
            <p className="mt-1 text-[11.5px] text-brand-muted">
              Integer from 1 to 100. Applied as bonus credits on top of the
              tier the buyer purchases.
            </p>
          </div>
          <div>
            <Label htmlFor="rc-expires">Expires at (optional)</Label>
            <Input
              id="rc-expires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
            <p className="mt-1 text-[11.5px] text-brand-muted">
              Leave blank for no expiry.
            </p>
          </div>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create code"}
          </Button>
          {duplicate && (
            <p className="text-[12.5px] text-destructive">
              That code is already taken. Pick a different code.
            </p>
          )}
          {create.error && !duplicate && (
            <p className="text-[12.5px] text-destructive">
              {create.error instanceof Error
                ? create.error.message
                : "Create failed"}
            </p>
          )}
          {success && (
            <p className="text-[12.5px] text-[oklch(0.45_0.087_155.7)]">
              {success}
            </p>
          )}
        </form>
      </section>

      <section>
        <h2 className="mb-3 font-serif text-[20px] text-brand-ink">
          Existing codes
        </h2>
        {rows.isLoading ? (
          <p className="text-[14px] text-brand-muted">Loading…</p>
        ) : !rows.data || rows.data.length === 0 ? (
          <p className="text-[14px] text-brand-muted">No codes yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-brand-line bg-brand-paper">
            <table className="w-full text-[14px]">
              <thead className="bg-brand-paper-2 text-left text-[11.5px] uppercase tracking-wider text-brand-muted">
                <tr>
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">Influencer</th>
                  <th className="px-3 py-2">Bonus %</th>
                  <th className="px-3 py-2">Active</th>
                  <th className="px-3 py-2">Expires</th>
                  <th className="px-3 py-2">Redemptions</th>
                  <th className="px-3 py-2">Credits granted</th>
                  <th className="px-3 py-2">Bonus credits</th>
                  <th className="px-3 py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {rows.data.map((r) => (
                  <CodeRow
                    key={r.id}
                    row={r}
                    onToggle={async (next) => {
                      await toggleFn({ data: { id: r.id, active: next } });
                      qc.invalidateQueries({
                        queryKey: ["admin-referral-codes"],
                      });
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}

function CodeRow({
  row,
  onToggle,
}: {
  row: ReferralCodeRow;
  onToggle: (next: boolean) => Promise<void>;
}) {
  const [busy, setBusy] = React.useState(false);
  return (
    <tr className="border-t border-brand-line">
      <td className="px-3 py-2 font-mono text-[13px]">{row.code}</td>
      <td className="px-3 py-2">{row.influencer_name}</td>
      <td className="px-3 py-2 tabular-nums">{row.bonus_pct}</td>
      <td className="px-3 py-2">
        <Switch
          checked={row.active}
          disabled={busy}
          onCheckedChange={async (v) => {
            setBusy(true);
            try {
              await onToggle(Boolean(v));
            } finally {
              setBusy(false);
            }
          }}
        />
      </td>
      <td className="px-3 py-2 text-brand-muted">
        {row.expires_at
          ? new Date(row.expires_at).toLocaleDateString()
          : "None"}
      </td>
      <td className="px-3 py-2 tabular-nums">{row.redemptions}</td>
      <td className="px-3 py-2 tabular-nums">{row.credits_granted_total}</td>
      <td className="px-3 py-2 tabular-nums">{row.bonus_credits_total}</td>
      <td className="px-3 py-2 text-brand-muted">
        {new Date(row.created_at).toLocaleDateString()}
      </td>
    </tr>
  );
}